import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { t } from '../i18n';
import { AppHeader } from './AppHeader';
import { Sidebar } from './Sidebar';
import { usePageTitle } from './usePageTitle';
import { useAuth } from '../hooks/useAuth';
import { ApiError, api } from '../services/api';
import { Button, LogoutIcon } from '../components/ui';

const SIDEBAR_COLLAPSED_KEY = 'balanceflow_sidebar_collapsed';

function readCollapsedPreference(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function AppLayout() {
  const title = usePageTitle();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, establishSession } = useAuth();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    readCollapsedPreference,
  );
  const [exitingSupport, setExitingSupport] = useState(false);
  const [supportExitError, setSupportExitError] = useState<string | null>(null);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileNavOpen]);

  function toggleSidebarCollapsed() {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // ignore storage failures
      }
      return next;
    });
  }

  async function exitSupport() {
    setExitingSupport(true);
    setSupportExitError(null);
    try {
      const result = await api.exitSupportLogin();
      establishSession(result.accessToken, result.user);
      navigate('/platform/tenants');
    } catch (err) {
      setSupportExitError(
        err instanceof ApiError ? err.message : t().common.error,
      );
    } finally {
      setExitingSupport(false);
    }
  }

  return (
    <div className="flex h-screen overflow-hidden bg-surface text-ink">
      {mobileNavOpen ? (
        <button
          type="button"
          aria-label={t().nav.closeMenu}
          className="fixed inset-0 z-30 bg-slate-950/50 backdrop-blur-[1px] lg:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      ) : null}

      <Sidebar
        mobileOpen={mobileNavOpen}
        collapsed={sidebarCollapsed}
        onNavigate={() => setMobileNavOpen(false)}
        onToggleCollapse={toggleSidebarCollapsed}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <AppHeader
          title={title}
          onOpenMenu={() => setMobileNavOpen(true)}
          onToggleSidebar={toggleSidebarCollapsed}
          sidebarCollapsed={sidebarCollapsed}
        />
        {user?.supportLogin ? (
          <div className="flex shrink-0 flex-col gap-1 border-b border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-700/50 dark:bg-amber-950/40 dark:text-amber-100 sm:px-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>
                {t().platform.supportBanner.replace(
                  '{name}',
                  user.tenantName || '—',
                )}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                loading={exitingSupport}
                onClick={() => void exitSupport()}
              >
                <LogoutIcon className="h-3.5 w-3.5" />
                {t().platform.supportExit}
              </Button>
            </div>
            {supportExitError ? (
              <p className="text-xs text-red-700 dark:text-red-300">
                {supportExitError}
              </p>
            ) : null}
          </div>
        ) : null}
        <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface p-3 sm:p-5 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
